import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Users, MapPin, DollarSign, Clock, Lock, FileText, Settings, Video, Ticket, CreditCard, Wallet, Receipt, History } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fundingSources, setFundingSources] = useState([]);
  const [loadingFundingSources, setLoadingFundingSources] = useState(true);
  const [expenseSummary, setExpenseSummary] = useState({ total_expense: 0, latest: [] });
  const [loadingExpenseSummary, setLoadingExpenseSummary] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userStr = localStorage.getItem('user');
    
    if (!token || !userStr) {
      toast.error('Akses ditolak');
      navigate('/admin/login');
      return;
    }
    
    try {
      const user = JSON.parse(userStr);
      if (user.role !== 'admin') {
        toast.error('Akses ditolak');
        navigate('/admin/login');
        return;
      }
    } catch (error) {
      toast.error('Akses ditolak');
      navigate('/admin/login');
      return;
    }

    loadStats();
    loadFundingSources();
    loadExpenseSummary();
  }, [navigate]);

  const loadStats = async () => {
    try {
      const response = await api.get('/admin/stats');
      setStats(response.data);
    } catch (error) {
      toast.error('Gagal memuat statistik');
    } finally {
      setLoading(false);
    }
  };

  const loadFundingSources = async () => {
    setLoadingFundingSources(true);
    try {
      const res = await api.get('/admin/funding-sources/balances');
      setFundingSources(Array.isArray(res.data) ? res.data : []);
    } catch (_) {
      setFundingSources([]);
      toast.error('Gagal memuat saldo sumber dana');
    } finally {
      setLoadingFundingSources(false);
    }
  };

  const formatCurrency = (value) => {
    const n = Number(value || 0);
    const v = Number.isFinite(n) ? n : 0;
    return v.toLocaleString('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 });
  };

  const loadExpenseSummary = async () => {
    setLoadingExpenseSummary(true);
    try {
      const res = await api.get('/admin/expenses/summary');
      setExpenseSummary(res.data || { total_expense: 0, latest: [] });
    } catch (_) {
      setExpenseSummary({ total_expense: 0, latest: [] });
      toast.error('Gagal memuat ringkasan pengeluaran');
    } finally {
      setLoadingExpenseSummary(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    toast.success('Logout berhasil');
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Admin Navbar */}
      <nav className="border-b border-zinc-800 bg-background-paper/50 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-3">
              <img src="/zero-logo.png" alt="Logo" className="w-10 h-10 object-contain rounded-md" />
              <span className="text-2xl font-heading font-black italic text-primary uppercase">Admin Panel</span>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/admin/change-password" className="text-zinc-400 hover:text-primary font-medium transition-colors flex items-center gap-2">
                <Lock className="w-4 h-4" />
                <span className="hidden sm:inline">Change Password</span>
              </Link>
              <button
                onClick={handleLogout}
                data-testid="admin-logout-btn"
                className="text-zinc-400 hover:text-red-500 font-medium transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="text-4xl font-heading font-bold italic uppercase text-white mb-8">Dashboard Admin</h1>

          {/* Stats Cards */}
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-zinc-900 border-l-4 border-primary rounded-r-lg p-6">
                <div className="flex items-center justify-between mb-2">
                  <Calendar className="w-8 h-8 text-primary" />
                  <span className="text-3xl font-black text-white">{stats?.total_bookings || 0}</span>
                </div>
                <div className="text-sm text-zinc-400 font-semibold uppercase">Total Booking</div>
              </div>

              <div className="bg-zinc-900 border-l-4 border-secondary rounded-r-lg p-6">
                <div className="flex items-center justify-between mb-2">
                  <Clock className="w-8 h-8 text-secondary" />
                  <span className="text-3xl font-black text-white">{stats?.pending_payments || 0}</span>
                </div>
                <div className="text-sm text-zinc-400 font-semibold uppercase">Pending Payment</div>
              </div>

              <div className="bg-zinc-900 border-l-4 border-primary rounded-r-lg p-6">
                <div className="flex items-center justify-between mb-2">
                  <Users className="w-8 h-8 text-primary" />
                  <span className="text-3xl font-black text-white">{stats?.total_users || 0}</span>
                </div>
                <div className="text-sm text-zinc-400 font-semibold uppercase">Total User</div>
              </div>

              <div className="bg-zinc-900 border-l-4 border-primary rounded-r-lg p-6">
                <div className="flex items-center justify-between mb-2">
                  <DollarSign className="w-8 h-8 text-primary" />
                  <span className="text-2xl font-black text-white">
                    {(stats?.total_revenue || 0).toLocaleString('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 })}
                  </span>
                </div>
                <div className="text-sm text-zinc-400 font-semibold uppercase">Total Revenue</div>
              </div>
            </div>
          )}

          {!loading && (
            <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6 mb-12">
              <div className="flex items-start justify-between gap-4 mb-5">
                <div>
                  <div className="text-lg font-bold text-white uppercase font-heading">Saldo Sumber Dana</div>
                  <div className="text-xs text-zinc-500 mt-1">Ringkasan saldo saat ini semua sumber dana aktif</div>
                </div>
                <Link to="/admin/sumber-dana" className="text-sm font-bold text-primary hover:text-primary/80 transition-colors">
                  Kelola
                </Link>
              </div>

              {loadingFundingSources ? (
                <div className="text-center py-6">
                  <div className="inline-block w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
                (() => {
                  const active = (fundingSources || []).filter((s) => !!s?.is_active);
                  const total = active.reduce((acc, s) => acc + (Number(s?.current_balance) || 0), 0);
                  const maxShow = 8;
                  const shown = active.slice(0, maxShow);
                  const remaining = Math.max(0, active.length - shown.length);

                  return (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                      <div className="bg-black border border-zinc-800 rounded-lg p-5">
                        <div className="flex items-center justify-between mb-2">
                          <Wallet className="w-8 h-8 text-primary" />
                          <div className="text-sm text-zinc-400 font-semibold uppercase">Total</div>
                        </div>
                        <div className="text-2xl font-black text-white">{formatCurrency(total)}</div>
                        <div className="text-xs text-zinc-500 mt-2">{active.length} sumber dana aktif</div>
                      </div>

                      <div className="lg:col-span-2 bg-black border border-zinc-800 rounded-lg p-5">
                        <div className="text-sm text-zinc-400 font-semibold uppercase mb-3">Rincian</div>
                        {shown.length ? (
                          <div className="space-y-3">
                            {shown.map((s) => (
                              <div key={s.id} className="flex items-center justify-between gap-4">
                                <div className="min-w-0">
                                  <div className="text-white font-bold truncate">{s?.name || '-'}</div>
                                  <div className="text-xs text-zinc-500 mt-1">
                                    {String(s?.type || '').toLowerCase() === 'cash' ? 'CASH' : 'BANK'}
                                  </div>
                                </div>
                                <div className="text-white font-black shrink-0">{formatCurrency(s?.current_balance)}</div>
                              </div>
                            ))}
                            {remaining ? (
                              <div className="text-xs text-zinc-500 pt-2">+{remaining} sumber dana lainnya</div>
                            ) : null}
                          </div>
                        ) : (
                          <div className="text-zinc-500 text-sm">Belum ada sumber dana aktif</div>
                        )}
                      </div>
                    </div>
                  );
                })()
              )}
            </div>
          )}

          {!loading && (
            <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6 mb-12">
              <div className="flex items-start justify-between gap-4 mb-5">
                <div>
                  <div className="text-lg font-bold text-white uppercase font-heading">Pengeluaran</div>
                  <div className="text-xs text-zinc-500 mt-1">Total pengeluaran & 5 pengeluaran terakhir</div>
                </div>
                <Link to="/admin/pengeluaran" className="text-sm font-bold text-primary hover:text-primary/80 transition-colors">
                  Lihat
                </Link>
              </div>

              {loadingExpenseSummary ? (
                <div className="text-center py-6">
                  <div className="inline-block w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
                (() => {
                  const latest = Array.isArray(expenseSummary?.latest) ? expenseSummary.latest : [];
                  const total = Number(expenseSummary?.total_expense || 0);
                  return (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                      <div className="bg-black border border-zinc-800 rounded-lg p-5">
                        <div className="flex items-center justify-between mb-2">
                          <Receipt className="w-8 h-8 text-secondary" />
                          <div className="text-sm text-zinc-400 font-semibold uppercase">Total</div>
                        </div>
                        <div className="text-2xl font-black text-white">{formatCurrency(total)}</div>
                        <div className="text-xs text-zinc-500 mt-2">Semua waktu</div>
                      </div>

                      <div className="lg:col-span-2 bg-black border border-zinc-800 rounded-lg p-5">
                        <div className="text-sm text-zinc-400 font-semibold uppercase mb-3">5 Terakhir</div>
                        {latest.length ? (
                          <div className="space-y-3">
                            {latest.map((e) => (
                              <div key={e.id} className="flex items-center justify-between gap-4">
                                <div className="min-w-0">
                                  <div className="text-white font-bold truncate">
                                    {(e?.expense_date || '').trim() || '-'}
                                    {e?.category ? ` • ${e.category}` : ''}
                                  </div>
                                  <div className="text-xs text-zinc-500 mt-1 truncate">{e?.description || '-'}</div>
                                </div>
                                <div className="text-white font-black shrink-0">{formatCurrency(e?.amount)}</div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-zinc-500 text-sm">Belum ada data pengeluaran</div>
                        )}
                      </div>
                    </div>
                  );
                })()
              )}
            </div>
          )}

          {/* Quick Actions */}
          <h2 className="text-2xl font-heading font-bold uppercase text-white mb-6">Menu Admin</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Link
              to="/admin/venues"
              data-testid="admin-venues-link"
              className="bg-zinc-900 border border-zinc-800 rounded-lg p-6 hover:border-primary/50 transition-all group"
            >
              <MapPin className="w-12 h-12 text-primary mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-xl font-bold text-white mb-2 uppercase font-heading">Kelola Lapangan</h3>
              <p className="text-zinc-400 text-sm">Tambah, edit, atau hapus lapangan futsal</p>
            </Link>

            <Link
              to="/admin/bookings"
              data-testid="admin-bookings-link"
              className="bg-zinc-900 border border-zinc-800 rounded-lg p-6 hover:border-primary/50 transition-all group"
            >
              <Calendar className="w-12 h-12 text-primary mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-xl font-bold text-white mb-2 uppercase font-heading">Kelola Booking</h3>
              <p className="text-zinc-400 text-sm">Verifikasi pembayaran dan kelola booking</p>
            </Link>

            <Link
              to="/admin/users"
              data-testid="admin-users-link"
              className="bg-zinc-900 border border-zinc-800 rounded-lg p-6 hover:border-primary/50 transition-all group"
            >
              <Users className="w-12 h-12 text-primary mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-xl font-bold text-white mb-2 uppercase font-heading">Kelola User</h3>
              <p className="text-zinc-400 text-sm">Lihat daftar customer yang terdaftar</p>
            </Link>

            <Link
              to="/admin/members"
              data-testid="admin-members-link"
              className="bg-zinc-900 border border-zinc-800 rounded-lg p-6 hover:border-primary/50 transition-all group"
            >
              <Users className="w-12 h-12 text-primary mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-xl font-bold text-white mb-2 uppercase font-heading">Kelola Member</h3>
              <p className="text-zinc-400 text-sm">Lihat dan kelola daftar member</p>
            </Link>

            <Link
              to="/admin/payment-config"
              data-testid="admin-payment-config-link"
              className="bg-zinc-900 border border-zinc-800 rounded-lg p-6 hover:border-primary/50 transition-all group"
            >
              <DollarSign className="w-12 h-12 text-primary mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-xl font-bold text-white mb-2 uppercase font-heading">Payment Config</h3>
              <p className="text-zinc-400 text-sm">Kelola nomor rekening dan QRIS</p>
            </Link>

            <Link
              to="/admin/sumber-dana"
              data-testid="admin-sumber-dana-link"
              className="bg-zinc-900 border border-zinc-800 rounded-lg p-6 hover:border-primary/50 transition-all group"
            >
              <Wallet className="w-12 h-12 text-primary mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-xl font-bold text-white mb-2 uppercase font-heading">Sumber Dana</h3>
              <p className="text-zinc-400 text-sm">Kelola akun bank & uang cash</p>
            </Link>

            <Link
              to="/admin/reports"
              data-testid="admin-reports-link"
              className="bg-zinc-900 border border-zinc-800 rounded-lg p-6 hover:border-primary/50 transition-all group"
            >
              <FileText className="w-12 h-12 text-primary mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-xl font-bold text-white mb-2 uppercase font-heading">Laporan</h3>
              <p className="text-zinc-400 text-sm">Lihat laporan transaksi & export PDF</p>
            </Link>

            <Link
              to="/admin/laporan-lapangan"
              data-testid="admin-laporan-lapangan-link"
              className="bg-zinc-900 border border-zinc-800 rounded-lg p-6 hover:border-primary/50 transition-all group"
            >
              <FileText className="w-12 h-12 text-primary mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-xl font-bold text-white mb-2 uppercase font-heading">Laporan Lapangan</h3>
              <p className="text-zinc-400 text-sm">Status slot jam (Booked/Kosong) per lapangan</p>
            </Link>

            <Link
              to="/admin/transactions"
              data-testid="admin-transactions-link"
              className="bg-zinc-900 border border-zinc-800 rounded-lg p-6 hover:border-primary/50 transition-all group"
            >
              <CreditCard className="w-12 h-12 text-primary mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-xl font-bold text-white mb-2 uppercase font-heading">Transaksi</h3>
              <p className="text-zinc-400 text-sm">List transaksi per tanggal & metode</p>
            </Link>

            <Link
              to="/admin/pengeluaran"
              data-testid="admin-pengeluaran-link"
              className="bg-zinc-900 border border-zinc-800 rounded-lg p-6 hover:border-primary/50 transition-all group"
            >
              <Receipt className="w-12 h-12 text-primary mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-xl font-bold text-white mb-2 uppercase font-heading">Pengeluaran</h3>
              <p className="text-zinc-400 text-sm">Catat pengeluaran operasional</p>
            </Link>

            <Link
              to="/admin/audit-logs"
              data-testid="admin-audit-logs-link"
              className="bg-zinc-900 border border-zinc-800 rounded-lg p-6 hover:border-primary/50 transition-all group"
            >
              <History className="w-12 h-12 text-primary mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-xl font-bold text-white mb-2 uppercase font-heading">Audit Logs</h3>
              <p className="text-zinc-400 text-sm">Riwayat perubahan data & aktivitas admin</p>
            </Link>

            <Link
              to="/admin/settings"
              data-testid="admin-settings-link"
              className="bg-zinc-900 border border-zinc-800 rounded-lg p-6 hover:border-primary/50 transition-all group"
            >
              <Settings className="w-12 h-12 text-primary mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-xl font-bold text-white mb-2 uppercase font-heading">Kelola Website</h3>
              <p className="text-zinc-400 text-sm">Atur pengaturan booking</p>
            </Link>

            <Link
              to="/admin/live-lapang"
              data-testid="admin-live-lapang-link"
              className="bg-zinc-900 border border-zinc-800 rounded-lg p-6 hover:border-primary/50 transition-all group"
            >
              <Video className="w-12 h-12 text-primary mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-xl font-bold text-white mb-2 uppercase font-heading">Live Lapang</h3>
              <p className="text-zinc-400 text-sm">Snapshot CCTV per lapangan (RTSP)</p>
            </Link>

            <Link
              to="/admin/vouchers"
              data-testid="admin-vouchers-link"
              className="bg-zinc-900 border border-zinc-800 rounded-lg p-6 hover:border-primary/50 transition-all group"
            >
              <Ticket className="w-12 h-12 text-primary mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-xl font-bold text-white mb-2 uppercase font-heading">Voucher</h3>
              <p className="text-zinc-400 text-sm">Buat voucher 1x pakai</p>
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AdminDashboard;
